import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_LINKS = {
  terms: { path: '/terminos', label: 'Términos y Condiciones' },
  privacy: { path: '/privacidad', label: 'Aviso de Privacidad' },
};
const PUBLIC_LEGAL_PATHS = ['/terminos', '/privacidad'];

/**
 * LegalGate — con sesión iniciada, pide aceptar los documentos legales que el
 * usuario tenga pendientes (cuentas creadas antes de que existieran, cuentas
 * creadas por invitación, o una versión nueva publicada por el admin).
 * No se puede cerrar sin aceptar; la alternativa es cerrar sesión.
 */
const LegalGate = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [documents, setDocuments] = useState([]);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/legal/status/me`);
      setDocuments(res.data?.documents || []);
    } catch {
      // Si no se puede consultar, no se bloquea al usuario.
      setDocuments([]);
    }
  }, []);

  useEffect(() => {
    setChecked(false);
    if (user?.id) load();
    else setDocuments([]);
  }, [user?.id, load]);

  if (!user || documents.length === 0 || PUBLIC_LEGAL_PATHS.includes(location.pathname)) {
    return null;
  }

  const accept = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/legal/accept`, {
        doc_types: documents.map((d) => d.doc_type),
      });
      if ((res.data?.pending || []).length === 0) {
        setDocuments([]);
        toast.success('Gracias. Tu aceptación quedó registrada.');
      } else {
        await load();
      }
    } catch {
      toast.error('No pudimos registrar tu aceptación. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-zinc-950/95 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-gate-title"
      data-testid="legal-gate"
    >
      <div className="w-full max-w-md rounded-sm border border-zinc-800 bg-zinc-900 p-6 space-y-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
          <div>
            <h2 id="legal-gate-title" className="font-teko text-3xl text-white uppercase leading-none">
              Antes de continuar
            </h2>
            <p className="text-zinc-400 text-sm mt-2">
              Para seguir usando ONOR Code necesitamos que leas y aceptes la versión vigente de:
            </p>
          </div>
        </div>

        <ul className="space-y-2">
          {documents.map((doc) => (
            <li key={doc.doc_type} className="flex items-center justify-between gap-3 text-sm">
              <a
                href={DOC_LINKS[doc.doc_type]?.path}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                {DOC_LINKS[doc.doc_type]?.label || doc.title}
              </a>
              <span className="text-zinc-500 text-xs">Versión {doc.version}</span>
            </li>
          ))}
        </ul>

        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            className="mt-0.5"
            checked={checked}
            onCheckedChange={(value) => setChecked(value === true)}
            data-testid="legal-gate-checkbox"
          />
          <span className="text-zinc-300 text-xs leading-relaxed">
            Leí y acepto los documentos anteriores, y otorgo mi consentimiento expreso para el
            tratamiento de mis datos personales, incluidos los de salud, conforme al Aviso de
            Privacidad.
          </span>
        </label>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            className="btn-primary flex-1"
            disabled={!checked || saving}
            onClick={accept}
            data-testid="legal-gate-accept"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Aceptar y continuar
          </Button>
          <Button type="button" variant="ghost" className="btn-secondary" onClick={logout}>
            Cerrar sesión
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LegalGate;
